import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export const VERSION_CHECK_INTERVAL_MS = 60_000;

const RUNNING_APP_VERSION =
  typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'development';

type FetchVersion = (signal?: AbortSignal) => Promise<string | null>;

export const fetchDeployedVersion: FetchVersion = async signal => {
  const response = await fetch(`/version.json?checked=${Date.now()}`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    return null;
  }

  const payload = await response.json();
  return typeof payload?.version === 'string' ? payload.version : null;
};

const reloadPage = () => window.location.reload();

type UpdateAvailableBadgeProps = {
  currentVersion?: string;
  fetchVersion?: FetchVersion;
  reload?: () => void;
  checkIntervalMs?: number;
};

const StatusRegion = styled.div`
  position: fixed;
  left: 50%;
  bottom: max(18px, calc(env(safe-area-inset-bottom) + 12px));
  z-index: 10000;
  transform: translateX(-50%);
  width: max-content;
  max-width: calc(100vw - 24px);
`;

const UpdateButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-height: 42px;
  padding: 7px 9px 7px 12px;
  border: 1px solid rgba(14, 59, 64, 0.24);
  border-inline-start: 4px solid #00b5ad;
  border-radius: 8px;
  background: var(--br-surface);
  color: var(--br-text);
  box-shadow: 0 7px 22px rgba(14, 59, 64, 0.2);
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  transition:
    box-shadow 150ms ease,
    transform 150ms ease;

  &:hover {
    box-shadow: 0 9px 26px rgba(14, 59, 64, 0.26);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid rgba(0, 181, 173, 0.38);
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const RefreshMark = styled.span`
  color: #008c86;
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
`;

const ActionLabel = styled.span`
  margin-inline-start: 2px;
  padding: 5px 8px;
  border-radius: 5px;
  background: #e4f7f5;
  color: #08746f;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
`;

const UpdateAvailableBadge: React.FC<UpdateAvailableBadgeProps> = ({
  currentVersion = RUNNING_APP_VERSION,
  fetchVersion = fetchDeployedVersion,
  reload = reloadPage,
  checkIntervalMs = VERSION_CHECK_INTERVAL_MS,
}) => {
  const { t } = useTranslation();
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (currentVersion === 'development') {
      return undefined;
    }

    let disposed = false;
    let checking = false;
    const controller = new AbortController();

    const checkForUpdate = async () => {
      if (checking || disposed) {
        return;
      }

      checking = true;
      try {
        const deployedVersion = await fetchVersion(controller.signal);
        if (
          !disposed &&
          deployedVersion &&
          deployedVersion !== currentVersion
        ) {
          setUpdateAvailable(true);
        }
      } catch {
        // A version check should never interrupt the app. The next interval,
        // focus, or visibility change will try again.
      } finally {
        checking = false;
      }
    };

    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };

    void checkForUpdate();
    const intervalId = window.setInterval(checkForUpdate, checkIntervalMs);
    window.addEventListener('focus', checkForUpdate);
    document.addEventListener('visibilitychange', checkWhenVisible);

    return () => {
      disposed = true;
      controller.abort();
      window.clearInterval(intervalId);
      window.removeEventListener('focus', checkForUpdate);
      document.removeEventListener('visibilitychange', checkWhenVisible);
    };
  }, [checkIntervalMs, currentVersion, fetchVersion]);

  if (!updateAvailable) {
    return null;
  }

  const newVersionLabel = t('NewVersion', 'New version');
  const updateLabel = t('Update', 'Update');

  return (
    <StatusRegion role="status" aria-live="polite">
      <UpdateButton
        type="button"
        onClick={reload}
        aria-label={`${newVersionLabel}. ${updateLabel}`}
      >
        <RefreshMark aria-hidden="true">↻</RefreshMark>
        <span>{newVersionLabel}</span>
        <ActionLabel aria-hidden="true">{updateLabel}</ActionLabel>
      </UpdateButton>
    </StatusRegion>
  );
};

export default UpdateAvailableBadge;
